<script setup lang="ts">
import { computed, watch } from 'vue';
import { store } from '@/app/store';
import { captureUpsell } from '@/services/telemetry/posthog';
// biome-ignore lint/correctness/noUnusedImports: used by Vue template
import CustomerExtraFile from './CustomerExtraFile.vue';
import { extraAnalyticsProperties, extraAnalyticsSession } from './extra-files';
import type { CustomerDigitalExtrasView } from './useCustomerDigitalExtras';

const props = defineProps<
  CustomerDigitalExtrasView & {
    receiptOnly?: boolean;
  }
>();
defineEmits<{ retry: [] }>();
const viewed = new Set<string>();
const money = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat(
    store.currentLocale?.code ?? 'en-GB',
    { style: 'currency', currency }
  );
  return formatter.format(
    amount / 10 ** (formatter.resolvedOptions().maximumFractionDigits ?? 2)
  );
};
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const totals = computed(() => {
  const amounts = new Map<string, number>();
  for (const extra of products.value) {
    if (extra.price_minor === 0) {
      continue;
    }
    amounts.set(
      extra.currency,
      (amounts.get(extra.currency) ?? 0) + extra.price_minor
    );
  }
  return [...amounts].map(([currency, amount]) => money(amount, currency));
});
// biome-ignore lint/correctness/noUnusedVariables: used by the Vue template
const hasAmountDue = computed(() =>
  props.extras.some((extra) => extra.price_minor > 0)
);
const products = computed(() =>
  props.extras.map((extra) => ({
    ...extra,
    video: extra.files.find((file) => file.id === 'video'),
    poster: extra.files.find((file) => file.id === 'poster'),
  }))
);

watch(
  () => props.extras,
  (extras) => {
    for (const extra of extras) {
      const sessionId = extraAnalyticsSession(extra);
      const key = `${extra.id}:${Boolean(props.receiptOnly)}`;
      if (!sessionId || viewed.has(key)) {
        continue;
      }
      viewed.add(key);
      captureUpsell(
        props.receiptOnly
          ? 'extras_cashier_order_viewed'
          : 'extras_customer_order_viewed',
        sessionId,
        {
          ...extraAnalyticsProperties(extra),
          status: extra.status,
        }
      );
    }
  },
  { immediate: true }
);
</script>

<template>
  <section class="DigitalExtras" :aria-label="$t('CustomerDigitalExtras.title')">
    <div v-if="loading" class="DigitalExtras__status" role="status"><p>{{ $t('CustomerDigitalExtras.loading') }}</p></div>
    <div v-else-if="failed" class="DigitalExtras__status" role="status"><p>{{ $t('CustomerDigitalExtras.loadError') }}</p><p v-if="downloadCode" class="DigitalExtras__reference">{{ $t('CustomerDigitalExtras.reference', { reference: downloadCode }) }}</p><button type="button" data-testid="customer-extras-retry" :disabled="fetching" @click="$emit('retry')">{{ $t(fetching ? 'CustomerDigitalExtras.loading' : 'CustomerDigitalExtras.retry') }}</button></div>
    <div v-else-if="!products.length" class="DigitalExtras__status" role="status"><p>{{ $t('CustomerDigitalExtras.empty') }}</p></div>
    <article v-if="receiptOnly && products.length" class="DigitalExtras__status DigitalExtras__cashier" data-testid="cashier-order">
      <h2>{{ $t('CustomerDigitalExtras.orderTitle') }}</h2>
      <p>{{ $t(hasAmountDue ? 'CustomerDigitalExtras.showCashier' : 'CustomerDigitalExtras.includedOrder') }}</p>
      <dl><template v-for="extra in products" :key="extra.id"><dt>{{ extra.title }}<small v-if="extra.status === 'failed'">{{ $t('CustomerDigitalExtras.failed') }}</small><small v-if="extra.status === 'failed'" class="DigitalExtras__reference">{{ $t('CustomerDigitalExtras.reference', { reference: downloadCode || extra.id }) }}</small></dt><dd>{{ extra.price_minor === 0 ? $t('CustomerDigitalExtras.included') : money(extra.price_minor, extra.currency) }}</dd></template></dl>
      <div class="DigitalExtras__cashier-total"><span>{{ $t('CustomerDigitalExtras.videoTotal') }}</span><strong>{{ hasAmountDue ? totals.join(' + ') : $t('CustomerDigitalExtras.included') }}</strong></div>
      <p v-if="products.some(extra => extra.payment_model === 'venue_counter' && extra.price_minor > 0)">{{ $t('CustomerDigitalExtras.payAtDesk') }}</p>
    </article>
    <template v-if="!receiptOnly">
    <div v-for="extra in products" :key="extra.id" class="DigitalExtras__order">
      <article v-if="extra.status !== 'ready'" class="DigitalExtras__status">
        <h2>{{ extra.title }}</h2>
        <template v-if="extra.status === 'queued' || extra.status === 'running'">
          <p role="status" aria-live="polite">{{ $t('CustomerDigitalExtras.preparing') }}</p>
          <p>{{ $t('CustomerDigitalExtras.preparingHelp') }}</p>
        </template>
        <template v-else>
          <p role="status">{{ $t('CustomerDigitalExtras.failed') }}</p>
          <p class="DigitalExtras__reference">{{ $t('CustomerDigitalExtras.reference', { reference: downloadCode || extra.id }) }}</p>
        </template>
      </article>
      <div v-else class="DigitalExtras__products">
        <CustomerExtraFile v-if="extra.video" :key="`${extra.id}-video`" :order-id="extra.id" :session-id="extraAnalyticsSession(extra)" :analytics="extraAnalyticsProperties(extra)" :file="extra.video" :poster="extra.poster?.url" />
        <div class="DigitalExtras__receipt">
          <span>{{ extra.title }}</span>
          <span class="DigitalExtras__ready"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>{{ $t('CustomerDigitalExtras.ready') }}</span>
        </div>
      </div>
    </div>
    </template>
  </section>
</template>

<style scoped>
.DigitalExtras, .DigitalExtras__products { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; min-width: 0; }
.DigitalExtras__status { border: 1px solid var(--Extras-line); border-radius: 20px; overflow: hidden; background: var(--Extras-panel); }
.DigitalExtras__status { padding: 18px; }
.DigitalExtras h2 { margin: 0; font: 800 25px / 1.1 var(--Download-display-font); font-variation-settings: 'wght' 800; letter-spacing: -.7px; }
.DigitalExtras__receipt { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 16px; padding: 17px 18px; border: 1px solid var(--Extras-line); border-radius: 16px; background: var(--Extras-panel); font-weight: 700; }
.DigitalExtras__ready { display: flex; align-items: center; gap: 6px; color: var(--Extras-accent); font-size: 11px; font-weight: 600; }
.DigitalExtras__ready svg { width: 17px; height: 17px; }
.DigitalExtras__status p { color: var(--Extras-muted); line-height: 1.6; }
.DigitalExtras__reference { overflow-wrap: anywhere; }
.DigitalExtras__status button { font: inherit; min-height: 44px; padding: 10px 18px; border: 0; border-radius: 99px; color: var(--Extras-on-accent); background: var(--Extras-accent); cursor: pointer; }
@media (min-width: 720px) {
  .DigitalExtras__products { width: min(520px, 100%); margin-inline: auto; align-items: start; gap: 22px; }
  .DigitalExtras__receipt { grid-column: 1 / -1; }
}
.DigitalExtras__cashier dl { display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 12px; margin: 16px 0; }
.DigitalExtras__cashier dd { margin: 0; font-weight: 800; }
.DigitalExtras__cashier small { display: block; }
.DigitalExtras__cashier-total { display: flex; justify-content: space-between; gap: 12px; padding-top: 14px; border-top: 1px solid var(--Extras-line); font-weight: 800; }
</style>
